/**
 * SiteClassificationSection.tsx — wires the vicinity fetch to the classification readout (T-512).
 *
 * The one stateful piece: fetch the vicinity payload for the site (useSiteVicinity), resolve the
 * containing block + block group (selectLocationClassification, pure), and hand both to the pure
 * LocationClassificationNote. Splitting it this way keeps every honest-labeling decision in
 * unit-tested pure code and leaves only the fetch/derive glue here.
 */
import { useMemo } from 'react'
import { useSiteVicinity } from '../hooks/useSiteVicinity'
import { selectLocationClassification } from '../lib/locationClassification'
import type { LocationClassification } from '../lib/locationClassification'
import { LocationClassificationNote } from './LocationClassificationNote'
import { SiteSpecificScores } from './SiteSpecificScores'

export interface SiteClassificationSectionProps {
  /** The searched site the readout describes. Null → the section renders nothing. */
  site: { lat: number; lng: number } | null
  /**
   * T-628: the classification resolved once by MapShell, so the panel and the compact card read
   * the SAME answer from ONE /api/vicinity request. When omitted this section falls back to
   * fetching for itself, which is how it behaved before and keeps it usable standalone.
   */
  classification?: LocationClassification | null
}

export function SiteClassificationSection({
  site,
  classification: injected,
}: SiteClassificationSectionProps) {
  // Only fetch when nothing was handed down — `null` from the parent means "resolved, nothing
  // here", which is a different statement from "not provided".
  const shouldFetch = injected === undefined
  const { vicinity, status } = useSiteVicinity(shouldFetch ? site : null)

  const own: LocationClassification | null = useMemo(
    () => (site && vicinity ? selectLocationClassification(site, vicinity) : null),
    [site, vicinity],
  )

  if (!site) return null

  const classification = shouldFetch ? own : injected
  const noteStatus = shouldFetch
    ? status === 'ready'
      ? 'ready'
      : status === 'error'
        ? 'error'
        : 'loading'
    : classification
      ? 'ready'
      : 'loading'
  return (
    <>
      <LocationClassificationNote status={noteStatus} classification={classification} />
    </>
  )
}
